<template>
  <AiEducationLayout>
    <div class="ai-edu-admin-page">
      <ContentWrap title="选课候补">
        <el-form :inline="true" :model="query" class="-mb-15px">
          <el-form-item label="学校">
            <el-select
              v-model="query.schoolId"
              class="!w-200px"
              clearable
              filterable
              @change="onSchoolChange"
            >
              <el-option v-for="s in schools" :key="s.id" :label="s.name" :value="s.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="学期">
            <el-select
              v-model="query.termId"
              class="!w-180px"
              clearable
              filterable
              @change="handleQuery"
            >
              <el-option v-for="t in terms" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="教学班">
            <el-select
              v-model="query.teachingClassId"
              class="!w-200px"
              clearable
              filterable
              @change="handleQuery"
            >
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select
              v-model="query.status"
              class="!w-140px"
              clearable
              @change="handleQuery"
            >
              <el-option
                v-for="d in getIntDictOptions(DICT_TYPE.AI_EDU_ENROLLMENT_STATUS)"
                :key="d.value"
                :label="d.label"
                :value="d.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
            <el-button
              v-hasPermi="['ai-education:enrollment:write']"
              plain
              type="primary"
              @click="openForceDialog"
            >
              <Icon icon="ep:plus" />强制选课
            </el-button>
            <el-button
              v-hasPermi="['ai-education:enrollment:write']"
              :disabled="!query.teachingClassId"
              plain
              type="success"
              @click="handlePromote"
            >
              候补晋级
            </el-button>
          </el-form-item>
        </el-form>
      </ContentWrap>
      <ContentWrap>
        <el-table v-loading="loading" :data="list" border>
          <el-table-column prop="teachingClassName" label="教学班" min-width="140" />
          <el-table-column prop="termName" label="学期" min-width="120" />
          <el-table-column label="学生" min-width="120">
            <template #default="{ row }">{{ studentLabel(row) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_ENROLLMENT_STATUS" :value="row.status" />
            </template>
          </el-table-column>
          <el-table-column prop="waitlistNo" label="候补号" width="90" />
          <el-table-column prop="enrollTime" label="选课时间" width="170" />
          <el-table-column prop="confirmTime" label="确认时间" width="170" />
          <el-table-column prop="dropTime" label="退课时间" width="170" />
          <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button
                v-if="row.status !== 30"
                v-hasPermi="['ai-education:enrollment:write']"
                link
                type="danger"
                @click="handleDrop(row.id)"
                >退课</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </ContentWrap>

      <Dialog v-model="forceVisible" title="强制选课" width="480px">
        <el-form ref="forceFormRef" :model="forceForm" :rules="forceRules" label-width="100px">
          <el-form-item label="教学班" prop="teachingClassId">
            <el-select v-model="forceForm.teachingClassId" class="w-100%" filterable>
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="学生" prop="studentId">
            <el-select
              v-model="forceForm.studentId"
              class="w-100%"
              filterable
              remote
              :remote-method="searchStudents"
              :loading="studentLoading"
              placeholder="搜索学生"
            >
              <el-option
                v-for="s in students"
                :key="s.id"
                :label="s.nickname || s.mobile || `#${s.id}`"
                :value="s.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="forceForm.remark" type="textarea" />
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="forceVisible = false">取消</el-button>
          <el-button :loading="saving" type="primary" @click="submitForce">确定</el-button>
        </template>
      </Dialog>
    </div>
  </AiEducationLayout>
</template>

<script lang="ts" setup>
import * as AiEducationApi from '@/api/aiEducation'
import * as UniversityApi from '@/api/aiEducation/university'
import AiEducationLayout from '../../layout/AiEducationLayout.vue'
import request from '@/config/axios'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'AiEduEnrollment' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const studentLoading = ref(false)
const list = ref<UniversityApi.EnrollmentVO[]>([])
const schools = ref<AiEducationApi.SchoolVO[]>([])
const terms = ref<UniversityApi.TermSimpleVO[]>([])
const teachingClasses = ref<AiEducationApi.TeachingClassVO[]>([])
const students = ref<Array<{ id: number; nickname?: string; mobile?: string }>>([])
const query = reactive<{
  schoolId?: number
  termId?: number
  teachingClassId?: number
  status?: number
}>({})
const forceVisible = ref(false)
const forceFormRef = ref<FormInstance>()
const forceForm = reactive<{ teachingClassId?: number; studentId?: number; remark?: string }>({})
const forceRules: FormRules = {
  teachingClassId: [{ required: true, message: '请选择教学班', trigger: 'change' }],
  studentId: [{ required: true, message: '请选择学生', trigger: 'change' }]
}

const studentLabel = (row: UniversityApi.EnrollmentVO) => {
  if (row.studentName) return row.studentName
  const s = students.value.find((item) => item.id === row.studentId)
  return s?.nickname || s?.mobile || `学生 #${row.studentId}`
}

const loadSchools = async () => {
  schools.value = (await AiEducationApi.getSchoolList()) || []
}
const onSchoolChange = async () => {
  query.termId = undefined
  query.teachingClassId = undefined
  terms.value = query.schoolId
    ? (await UniversityApi.getTermSimpleList(query.schoolId)) || []
    : []
  teachingClasses.value = query.schoolId
    ? (await AiEducationApi.getTeachingClassList({ schoolId: query.schoolId })) || []
    : []
  await load()
}
const searchStudents = async (keyword = '') => {
  studentLoading.value = true
  try {
    const result: any = await request.get({
      url: '/member/user/page',
      params: { pageNo: 1, pageSize: 30, nickname: keyword || undefined }
    })
    students.value = result?.list || result || []
  } finally {
    studentLoading.value = false
  }
}
const load = async () => {
  loading.value = true
  try {
    list.value =
      (await UniversityApi.getEnrollmentList({
        teachingClassId: query.teachingClassId,
        termId: query.termId,
        status: query.status
      })) || []
    await searchStudents()
  } finally {
    loading.value = false
  }
}
const handleQuery = () => load()
const openForceDialog = async () => {
  forceForm.teachingClassId = query.teachingClassId
  forceForm.studentId = undefined
  forceForm.remark = ''
  if (!teachingClasses.value.length && query.schoolId) {
    teachingClasses.value =
      (await AiEducationApi.getTeachingClassList({ schoolId: query.schoolId })) || []
  }
  await searchStudents()
  forceVisible.value = true
}
const submitForce = async () => {
  await forceFormRef.value?.validate()
  saving.value = true
  try {
    await UniversityApi.forceEnroll({
      teachingClassId: forceForm.teachingClassId!,
      studentId: forceForm.studentId!,
      remark: forceForm.remark
    })
    message.success('选课成功')
    forceVisible.value = false
    await load()
  } finally {
    saving.value = false
  }
}
const handlePromote = async () => {
  if (!query.teachingClassId) return
  await UniversityApi.promoteWaitlist(query.teachingClassId)
  message.success('候补晋级已执行')
  await load()
}
const handleDrop = async (enrollmentId: number) => {
  await message.confirm('确认退课？')
  await UniversityApi.dropEnrollment(enrollmentId)
  message.success('已退课')
  await load()
}

onMounted(async () => {
  await loadSchools()
  await load()
})
</script>
